import { isAfter, addSeconds } from "date-fns"

/**
 * Validate JWT token format
 * @param {string} token - Token to validate
 * @returns {boolean} - True if token appears to be a valid JWT
 */
const isValidJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return false
  }
  
  // JWT format: header.payload.signature (3 parts separated by dots)
  const parts = token.split('.')
  if (parts.length !== 3) {
    return false
  }
  
  // Each part should be base64url encoded (alphanumeric, -, _)
  const base64UrlPattern = /^[A-Za-z0-9_-]+$/
  return parts.every(part => part.length > 0 && base64UrlPattern.test(part))
}

/**
 * Validate expiry value is a positive integer
 * @param {string} expiresIn - Expiry value from OAuth response
 * @returns {boolean} - True if valid
 */
const isValidExpiry = (expiresIn) => {
  if (!expiresIn) {
    return false
  }
  
  const parsed = parseInt(expiresIn, 10)
  return !isNaN(parsed) && parsed > 0 && parsed < 86400 // Max 24 hours
}

const shouldAuth = () => {
  return (!isAuthenticated() && !isHandshakeReady())
}

const isAuthenticated = () => {
  const accessToken = localStorage.getItem("accessToken")
  const expiry = localStorage.getItem("expiry")

  if (accessToken && isAfter(new Date(expiry), new Date())) {
    return true
  }

  return false
}

const isHandshakeReady = () => {
  const params = new URLSearchParams(window.location.hash.replace("#", ""))

  return params.has("access_token") && params.has("id_token")
}

const tokens = () => {
  const idToken = localStorage.getItem("idToken")
  const accessToken = localStorage.getItem("accessToken")
  const expiry = localStorage.getItem("expiry")

  if (accessToken && isAfter(new Date(expiry), new Date())) {
    return {
      accessToken,
      idToken
    }
  }

  const params = new URLSearchParams(window.location.hash.replace("#", ""))
  
  // Validate tokens before storage to prevent malicious data injection
  const accessTokenParam = params.get("access_token")
  const idTokenParam = params.get("id_token")
  const expiresInParam = params.get("expires_in")
  
  // Validate access token format
  if (!isValidJWT(accessTokenParam)) {
    console.error("Invalid access token format received from OAuth provider")
    throw new Error("Invalid access token format")
  }
  
  // Validate ID token format
  if (!isValidJWT(idTokenParam)) {
    console.error("Invalid ID token format received from OAuth provider")
    throw new Error("Invalid ID token format")
  }
  
  // Validate expiry value
  if (!isValidExpiry(expiresInParam)) {
    console.error("Invalid expires_in value received from OAuth provider")
    throw new Error("Invalid token expiry value")
  }
  
  // Store validated tokens
  try {
    localStorage.setItem("accessToken", accessTokenParam)
    localStorage.setItem("idToken", idTokenParam)
    localStorage.setItem("expiry", addSeconds(new Date(), expiresInParam))
  } catch (error) {
    console.error("Failed to store tokens in localStorage:", error)
    throw new Error("Token storage failed")
  }

  return {
    accessToken: accessTokenParam,
    idToken: idTokenParam
  }
}

export { shouldAuth, tokens }